import React, { useEffect, useState } from 'react';
import type { TimerBadgeProps, TimerSeverity } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function elapsedMinutes(startedAt: string): number {
  return Math.floor((Date.now() - new Date(startedAt).getTime()) / 60000);
}

function getSeverity(
  mins: number,
  warningMinutes: number,
  dangerMinutes: number,
): TimerSeverity {
  if (mins >= dangerMinutes) return 'critical';
  if (mins >= warningMinutes) return 'warning';
  return 'normal';
}

const SEVERITY_CLASSES: Record<TimerSeverity, string> = {
  normal:   'text-neutral-500 dark:text-neutral-400',
  warning:  'text-warning-600 dark:text-warning-400',
  critical: 'text-danger-600 dark:text-danger-400 animate-pulse',
};

const SIZE_CLASSES = {
  sm: 'text-xs gap-0.5',
  md: 'text-sm gap-1',
};

// ─── TimerBadge ───────────────────────────────────────────────────────────────

export function TimerBadge({
  startedAt,
  thresholdWarningSeconds = 300,  // 5 min
  thresholdCriticalSeconds = 600, // 10 min
  className,
}: TimerBadgeProps) {
  const warningMinutes = Math.floor(thresholdWarningSeconds / 60);
  const dangerMinutes  = Math.floor(thresholdCriticalSeconds / 60);

  const [mins, setMins] = useState(() => elapsedMinutes(startedAt));

  useEffect(() => {
    setMins(elapsedMinutes(startedAt));
    const id = setInterval(() => setMins(elapsedMinutes(startedAt)), 60_000);
    return () => clearInterval(id);
  }, [startedAt]);

  const severity = getSeverity(mins, warningMinutes, dangerMinutes);

  return (
    <span
      role="timer"
      aria-label={`${mins} minutes`}
      className={[
        'inline-flex items-center font-semibold',
        SIZE_CLASSES['sm'],
        SEVERITY_CLASSES[severity],
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Clock icon */}
      <svg
        className="h-3.5 w-3.5 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      {mins}m
    </span>
  );
}
